import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import {Add, Delete} from "@mui/icons-material";
import {
  calculatePartialHarvestBiomass,
  calculateStandingBiomass,
  calculateTotalBiomass,
  calculateFCR,
  estimateSurvival,
  PartialHarvest,
} from "../../utils/shrimpPerformance";
import {fetchTemplateNames, fetchAllTemplates} from "../../api/feed";

const defaultPartialHarvest: PartialHarvest = {count: 0, abw: 0};

interface PondData {
  currentCycle?: {
    totalSeed: number;
    startedAt: string;
  };
  performanceMetrics?: {
    totalFeed: number;
    currentStock: number;
    abw: number;
    age: number;
    totalHarvested: number;
    totalHarvestedWeight: number;
    totalMortality: number;
  };
}

interface ShrimpPerformanceCalculatorProps {
  pondData?: PondData;
}

const ShrimpPerformanceCalculator: React.FC<ShrimpPerformanceCalculatorProps> = ({pondData}) => {
  // Initialize with pond data if available, otherwise use defaults
  const initialStockingNumber = Number(pondData?.currentCycle?.totalSeed) || 100000;
  const initialTotalFeedUsed = Number(pondData?.performanceMetrics?.totalFeed) || 10000;
  const initialCurrentABW = Number(pondData?.performanceMetrics?.abw) || 25;
  const initialCurrentDOC = Number(pondData?.performanceMetrics?.age) || 0;

  // Calculate initial survival based on current stock vs initial stock
  const initialSurvival =
    pondData?.currentCycle?.totalSeed && pondData?.performanceMetrics?.currentStock
      ? (Number(pondData.performanceMetrics.currentStock) / Number(pondData.currentCycle.totalSeed)) * 100
      : 80;

  const [stockingNumber, setStockingNumber] = useState<number>(initialStockingNumber);
  const [totalFeedUsed, setTotalFeedUsed] = useState<number>(initialTotalFeedUsed);
  const [currentABW, setCurrentABW] = useState<number>(initialCurrentABW);
  const [survival, setSurvival] = useState<number>(initialSurvival);
  const [targetFCR, setTargetFCR] = useState<string>("");
  const [partialHarvests, setPartialHarvests] = useState<PartialHarvest[]>([]);

  // Template integration
  const [templateNames, setTemplateNames] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [allTemplates, setAllTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentDOC, setCurrentDOC] = useState<number>(initialCurrentDOC);
  const [predictedFCR, setPredictedFCR] = useState<number | null>(null);
  const [templateData, setTemplateData] = useState<any>(null);

  // Initialize partial harvests from pond data if available
  useEffect(() => {
    if (pondData?.performanceMetrics?.totalHarvested && pondData?.performanceMetrics?.totalHarvestedWeight) {
      const totalHarvested = Number(pondData.performanceMetrics.totalHarvested);
      const totalHarvestedWeight = Number(pondData.performanceMetrics.totalHarvestedWeight);

      if (totalHarvested > 0 && totalHarvestedWeight > 0) {
        // Calculate average ABW for harvested shrimp
        const avgHarvestABW = (totalHarvestedWeight * 1000) / totalHarvested; // Convert kg to grams

        setPartialHarvests([
          {
            count: totalHarvested,
            abw: avgHarvestABW,
          },
        ]);
      }
    }
  }, [pondData]);

  // Load template names and data
  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      try {
        const names = await fetchTemplateNames();
        const templates = await fetchAllTemplates();
        setTemplateNames(names);
        setAllTemplates(templates);
      } catch (error) {
        console.error("Error loading templates:", error);
      } finally {
        setLoading(false);
      }
    };
    loadTemplates();
  }, []);

  // Calculate predicted FCR when template or DOC changes
  useEffect(() => {
    if (selectedTemplate && currentDOC > 0) {
      const template = allTemplates.find((t) => t.templateName === selectedTemplate && t.doc === currentDOC);

      if (template) {
        setTemplateData(template);
        // Use template's expected FCR as prediction
        setPredictedFCR(template.fcrPercentage || null);
      } else {
        setTemplateData(null);
        setPredictedFCR(null);
      }
    } else {
      setTemplateData(null);
      setPredictedFCR(null);
    }
  }, [selectedTemplate, currentDOC, allTemplates]);

  // Add a new partial harvest row
  const addPartialHarvest = () => {
    setPartialHarvests([...partialHarvests, {...defaultPartialHarvest}]);
  };

  // Remove a partial harvest row
  const removePartialHarvest = (idx: number) => {
    setPartialHarvests(partialHarvests.filter((_, i) => i !== idx));
  };

  // Update a partial harvest value
  const updatePartialHarvest = (idx: number, field: keyof PartialHarvest, value: number) => {
    setPartialHarvests(partialHarvests.map((ph, i) => (i === idx ? {...ph, [field]: value} : ph)));
  };

  // Calculate values
  const survivalDecimal = survival / 100;
  const partialBiomass = calculatePartialHarvestBiomass(partialHarvests);
  let standingBiomass = calculateStandingBiomass(stockingNumber, survivalDecimal, currentABW);
  let totalBiomass = calculateTotalBiomass(partialBiomass, standingBiomass);
  let fcr = calculateFCR(totalFeedUsed, totalBiomass);
  let estimatedSurvival = survivalDecimal;

  // If target FCR is provided, estimate survival
  if (targetFCR && !isNaN(Number(targetFCR)) && Number(targetFCR) > 0) {
    estimatedSurvival = estimateSurvival(stockingNumber, totalFeedUsed, partialHarvests, currentABW, Number(targetFCR));
    standingBiomass = calculateStandingBiomass(stockingNumber, estimatedSurvival, currentABW);
    totalBiomass = calculateTotalBiomass(partialBiomass, standingBiomass);
    fcr = calculateFCR(totalFeedUsed, totalBiomass);
  }

  return (
    <Box maxWidth={800} mx="auto" mt={4}>
      <Typography variant="h5" gutterBottom fontWeight={600} color="primary">
        Shrimp Performance Calculator with Template Integration
      </Typography>

      {/* Prefilled Data Notice */}
      {pondData && (
        <Alert severity="info" sx={{mb: 3}}>
          <Typography variant="subtitle2" gutterBottom>
            📊 Calculator Prefilled with Current Pond Data
          </Typography>
          <Typography variant="body2">
            All fields have been automatically populated with your pond's current performance data. You can modify any values to run different
            scenarios.
          </Typography>
        </Alert>
      )}

      {/* Template Selection Section */}
      <Paper sx={{p: 2, mb: 3, backgroundColor: "#f8f9fa"}}>
        <Typography variant="h6" gutterBottom color="primary">
          Template-Based FCR Prediction
        </Typography>

        {loading ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography>Loading templates...</Typography>
          </Box>
        ) : (
          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl sx={{minWidth: 200}}>
              <InputLabel>Select Feed Template</InputLabel>
              <Select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} label="Select Feed Template">
                <MenuItem value="">
                  <em>Choose a template</em>
                </MenuItem>
                {templateNames.map((template) => (
                  <MenuItem key={template.templateName} value={template.templateName}>
                    {template.templateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Current DOC (Days)"
              type="number"
              value={currentDOC}
              onChange={(e) => setCurrentDOC(Number(e.target.value))}
              InputProps={{inputProps: {min: 0}}}
              sx={{minWidth: 150}}
            />
          </Box>
        )}

        {/* Template Data Display */}
        {templateData && (
          <Box mt={2}>
            <Alert severity="info" sx={{mb: 2}}>
              <Typography variant="subtitle2" gutterBottom>
                Template Data for DOC {currentDOC}:
              </Typography>
              <Typography variant="body2">
                Expected ABW: {templateData.abw?.toFixed(2) || "N/A"} g | Expected Feed: {templateData.feedPercentage?.toFixed(2) || "N/A"}% |
                Expected FCR: {templateData.fcrPercentage?.toFixed(3) || "N/A"}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* FCR Prediction */}
        {predictedFCR !== null && (
          <Alert severity="success" sx={{mt: 2}}>
            <Typography variant="subtitle2">
              Predicted FCR from Template: <strong>{predictedFCR.toFixed(3)}</strong>
            </Typography>
          </Alert>
        )}
      </Paper>

      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label="Stocking Number"
          type="number"
          value={stockingNumber}
          onChange={(e) => setStockingNumber(Number(e.target.value))}
          InputProps={{inputProps: {min: 0}}}
          helperText={pondData ? "Prefilled from pond's initial stocking" : ""}
        />
        <TextField
          label="Total Feed Used (kg)"
          type="number"
          value={totalFeedUsed}
          onChange={(e) => setTotalFeedUsed(Number(e.target.value))}
          InputProps={{inputProps: {min: 0, step: 0.01}}}
          helperText={pondData ? "Prefilled from pond's total feed consumption" : ""}
        />
        <TextField
          label="Current ABW (g)"
          type="number"
          value={currentABW}
          onChange={(e) => setCurrentABW(Number(e.target.value))}
          InputProps={{inputProps: {min: 0, step: 0.01}}}
          helperText={pondData ? "Prefilled from latest sampling data" : ""}
        />
        <TextField
          label="Survival (%)"
          type="number"
          value={survival}
          onChange={(e) => setSurvival(Number(e.target.value))}
          InputProps={{inputProps: {min: 0, max: 100, step: 0.01}}}
          helperText={pondData ? "Calculated from current stock vs initial stock" : "If Target FCR is provided, this will be estimated."}
        />
        <TextField
          label="Target FCR (optional)"
          type="number"
          value={targetFCR}
          onChange={(e) => setTargetFCR(e.target.value)}
          InputProps={{inputProps: {min: 0, step: 0.001}, endAdornment: <InputAdornment position="end">(auto-calc survival)</InputAdornment>}}
        />
      </Box>

      {/* FCR Comparison */}
      {predictedFCR !== null && fcr > 0 && (
        <Paper sx={{p: 2, mt: 2, backgroundColor: "#fff3e0"}}>
          <Typography variant="h6" gutterBottom color="warning.main">
            FCR Comparison
          </Typography>
          <Box display="flex" gap={4}>
            <Typography>
              <strong>Actual FCR:</strong> {fcr.toFixed(3)}
            </Typography>
            <Typography>
              <strong>Predicted FCR:</strong> {predictedFCR.toFixed(3)}
            </Typography>
            <Typography>
              <strong>Difference:</strong> {(fcr - predictedFCR).toFixed(3)}
            </Typography>
          </Box>
        </Paper>
      )}

      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          Partial Harvests
        </Typography>
        {pondData?.performanceMetrics?.totalHarvested && (
          <Alert severity="info" sx={{mb: 2}}>
            <Typography variant="body2">
              Partial harvests have been prefilled from your pond's harvest records. Total harvested: {pondData.performanceMetrics.totalHarvested}{" "}
              shrimp ({Number(pondData.performanceMetrics.totalHarvestedWeight).toFixed(2)} kg)
            </Typography>
          </Alert>
        )}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Number Harvested</TableCell>
                <TableCell>ABW at Harvest (g)</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partialHarvests.map((ph, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      type="number"
                      value={ph.count}
                      onChange={(e) => updatePartialHarvest(idx, "count", Number(e.target.value))}
                      InputProps={{inputProps: {min: 0}}}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={ph.abw}
                      onChange={(e) => updatePartialHarvest(idx, "abw", Number(e.target.value))}
                      InputProps={{inputProps: {min: 0, step: 0.01}}}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => removePartialHarvest(idx)} disabled={partialHarvests.length === 1}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Button startIcon={<Add />} onClick={addPartialHarvest} size="small">
                    Add Partial Harvest
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          Results
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Partial Harvest Biomass (kg)</TableCell>
                <TableCell>{partialBiomass.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Standing Biomass (kg)</TableCell>
                <TableCell>{standingBiomass.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Total Biomass Produced (kg)</TableCell>
                <TableCell>{totalBiomass.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>FCR</TableCell>
                <TableCell>{fcr > 0 ? fcr.toFixed(3) : "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Survival (%)</TableCell>
                <TableCell>{(estimatedSurvival * 100).toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ShrimpPerformanceCalculator;
